import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ListItemText, Box, Typography, Tooltip } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import ClearIcon from "@mui/icons-material/Clear";
import SearchWithDebounce from "../SearchWithDebounce";
import { FixedSizeList } from "react-window";
import { TooltipProps } from "../../../../helpers/materialUITheme";
import { Arrow } from "../../icons/Arrow";

const ITEM_HEIGHT = 45;
const ITEM_PADDING_TOP = 8;
const VISIBLE_ITEMS = 5;
const SEARCH_HEIGHT = 75;

const SelectSimpleInbuilt = ({
  options,
  label,
  onChange,
  error,
  value,
  withEmptyField = false,
  bgcolor,
  setValue,
  writeSearchDataToSessionStorage,
  storageValue,
  showSearch = false,
  ...rest
}) => {
  const { t } = useTranslation();
  const [currentValue, setCurrentValue] = useState(withEmptyField ? value : value || t(label));
  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options || []);
  const [isOpen, setIsOpen] = useState(false);
  const availableWidth = 600 - 40 - 16 - 24;

  const measureTextWidth = (text, font = "16px Inter") => {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    context.font = font;
    return context.measureText(text).width;
  };

  const MenuProps = {
    PaperProps: {
      style: {
        minWidth: "600px",
        maxHeight:
          filteredOptions?.length >= VISIBLE_ITEMS
            ? (ITEM_HEIGHT + ITEM_PADDING_TOP) * VISIBLE_ITEMS + SEARCH_HEIGHT
            : (ITEM_HEIGHT + ITEM_PADDING_TOP) * (options?.length + 1) + SEARCH_HEIGHT,
      },
    },
  };

  useEffect(() => {
    setFilteredOptions(options || []);
  }, [options]);

  useEffect(() => {
    if (searchValue) {
      const filtered = (options || []).filter((option) => {
        const optionText = (option.name?.toString() || option || "").toLowerCase();
        return optionText.includes(searchValue.toLowerCase());
      });
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options || []);
    }
  }, [searchValue, options]);

  const handleClose = () => {
    setIsOpen(false);
    setSearchValue(""); // Очищаем поиск при закрытии
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleChange = (v) => {
    const updatedValue = v === t(label) ? (withEmptyField ? "" : v) : v;
    writeSearchDataToSessionStorage && writeSearchDataToSessionStorage(storageValue, updatedValue);
    setCurrentValue(updatedValue);
    onChange(v === t(label) ? "" : v);
    handleClose();
  };

  const handleClearValue = () => {
    setCurrentValue(t(label) || "");
    writeSearchDataToSessionStorage(storageValue, "");
    onChange("");
  };

  const renderMenuItems = useCallback(
    ({ index, style }) => {
      const option = filteredOptions[index];

      const text = option?.name;
      const textWidth = measureTextWidth(text);
      const isOverflowed = textWidth > availableWidth;

      return (
        <MenuItem
          key={index}
          value={option.value || option.id}
          style={style}
          onClick={() => handleChange(option.value || option.id)}
        >
          {isOverflowed ? (
            <Tooltip title={text} componentsProps={TooltipProps} followCursor placement="bottom" arrow>
              <ListItemText primary={option.name} />
            </Tooltip>
          ) : (
            <ListItemText primary={option.name} />
          )}
        </MenuItem>
      );
    },
    [filteredOptions]
  );

  return (
    <FormControl fullWidth {...rest} sx={{ background: bgcolor ? bgcolor : "inherit", height: "24px" }}>
      {!withEmptyField && <InputLabel>{t(label)}</InputLabel>}
      <Select
        value={currentValue}
        open={isOpen}
        displayEmpty
        inputProps={{ "aria-label": "Without label" }}
        onOpen={handleOpen}
        onClose={handleClose}
        IconComponent={() => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => {
              currentValue !== t(label) ? handleClearValue() : setIsOpen((prev) => !prev);
            }}
          >
            {currentValue !== t(label) ? <ClearIcon sx={{ fontSize: 22 }} /> : <Arrow />}
          </Box>
        )}
        renderValue={(value) => {
          const { name } = filteredOptions.find((option) => option.value === value || option.id === value) || {};
          return (
            <ListItemText primary={value === t(label) ? (withEmptyField && !currentValue ? name : t(label)) : name} />
          );
        }}
        MenuProps={{
          ...MenuProps,
          variant: "menu",
          shouldFocus: false,
          autoFocus: false,
          disableAutoFocusItem: true,
          disableListFocus: true,
        }}
        sx={{ padding: "0px", backgroundColor: "transparent", ".MuiSelect-select": { border: "none" } }}
      >
        {showSearch && <SearchWithDebounce searchValue={searchValue} setSearchValue={setSearchValue} />}
        {filteredOptions?.length > 0 ? (
          <FixedSizeList
            height={Math.min(filteredOptions.length, VISIBLE_ITEMS) * ITEM_HEIGHT}
            itemCount={filteredOptions.length}
            itemSize={ITEM_HEIGHT}
            width="600px"
            style={{ overflowX: "hidden" }}
          >
            {renderMenuItems}
          </FixedSizeList>
        ) : (
          <MenuItem disabled sx={{ height: ITEM_HEIGHT + ITEM_PADDING_TOP }}>
            <ListItemText primary={<Typography>{t("label:no_results")}</Typography>} />
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

export default SelectSimpleInbuilt;
